import { useCallback, useEffect } from 'react';

// Use 'polite' for most messages. Use 'assertive' when
// visually you choose 'danger' as the message variant.
// It interrupts the user, so is for urgent errors only.
export type AriaLiveTypes = 'polite' | 'assertive';

// One region per assertiveness, so a message never has to mutate aria-live on the region
// it is about to announce from. Modal.tsx renders the same pair inside its dialog.
export const LIVE_REGION_TYPES: readonly AriaLiveTypes[] = ['polite', 'assertive'];

// Marks the live regions we own
export const LIVE_REGION_ATTRIBUTE = 'data-aria-live-region';

// The most recent announcement made from a region, and when it was made. Kept on the region
// so the record survives the message node, which is removed again after MESSAGE_LIFETIME.
export const LAST_MESSAGE_ATTRIBUTE = 'data-last-message';
export const LAST_MESSAGE_AT_ATTRIBUTE = 'data-last-message-at';

// How long a recorded announcement is trusted for. A repeat of the same message inside this
// window is swallowed, so a component re-rendering on the same state does not read the same
// sentence out twice. Past it the record counts as stale and the repeat is announced again —
// by then the user has most likely moved on and the message is news to them once more.
const DUPLICATE_WINDOW = 10000;

// How long an announced message stays in the DOM. This is housekeeping only — it keeps stale
// text out of the accessibility tree for anyone browsing with a virtual cursor. Announcing
// does not depend on it, because each message is announced when its node is added.
const MESSAGE_LIFETIME = 7000;

// How long to wait before filling a region we had to create ourselves. Assistive tech has to
// be monitoring the region before its content changes, otherwise there is nothing to announce.
const NEW_REGION_DELAY = 100;

// Copypasta to hide an element without hiding it for screen readers
// Another example: https://benmyers.dev/blog/native-visually-hidden/
export const visuallyHiddenStyle: React.CSSProperties = {
  position: 'absolute',
  visibility: 'visible',
  overflow: 'hidden',
  display: 'block',
  width: '1px',
  height: '1px',
  margin: '-1px',
  border: '0',
  padding: '0',
  clip: 'rect(0px, 0px, 0px, 0px)',
  clipPath: 'polygon(0px 0px, 0px 0px, 0px 0px, 0px 0px)',
  whiteSpace: 'nowrap',
};

function regionSelector(type: AriaLiveTypes) {
  return `:scope > [${LIVE_REGION_ATTRIBUTE}="${type}"]`;
}

function createLiveRegion(host: HTMLElement, type: AriaLiveTypes) {
  const region = document.createElement('div');

  region.setAttribute(LIVE_REGION_ATTRIBUTE, type);
  // A log announces each node added to it, so an identical repeated message is still read out,
  // and two messages in quick succession queue up instead of overwriting one another.
  region.setAttribute('role', 'log');
  region.setAttribute('aria-relevant', 'additions');
  // Live from the start: a region has to be observed as live *before* its content changes,
  // so a region created and filled in one go announces nothing.
  region.ariaLive = type;
  Object.assign(region.style, visuallyHiddenStyle);
  host.appendChild(region);

  return region;
}

// `aria-modal="true"` tells assistive tech that everything outside the dialog is unavailable,
// so regions on <body> are pruned away and never announced while a modal is open. Target the
// regions inside the topmost open dialog instead.
// https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Reference/Attributes/aria-modal
function getLiveRegion(type: AriaLiveTypes) {
  const dialogs = document.querySelectorAll<HTMLElement>('[role="dialog"][aria-modal="true"]');
  const host = dialogs[dialogs.length - 1] ?? document.body;

  // Direct children only, so a nested dialog's regions are not picked up
  const existing = host.querySelector<HTMLElement>(regionSelector(type));

  if (existing) {
    return { region: existing, created: false };
  }

  // The host has no region of its own yet, which is the normal case for a dialog
  return { region: createLiveRegion(host, type), created: true };
}

function isDuplicateAnnouncement(region: HTMLElement, message: string) {
  if (region.getAttribute(LAST_MESSAGE_ATTRIBUTE) !== message) {
    return false;
  }

  const announcedAt = Number(region.getAttribute(LAST_MESSAGE_AT_ATTRIBUTE));

  // Missing, empty or unparseable: without a usable timestamp there is no way to tell a fresh
  // repeat from a stale one, so let the message through rather than silently dropping it.
  if (!announcedAt) {
    return false;
  }

  return Date.now() - announcedAt <= DUPLICATE_WINDOW;
}

function recordAnnouncement(region: HTMLElement, message: string) {
  region.setAttribute(LAST_MESSAGE_ATTRIBUTE, message);
  region.setAttribute(LAST_MESSAGE_AT_ATTRIBUTE, String(Date.now()));
}

export default function useAriaAnnouncer() {
  // Create the live regions in case they do not exist yet
  useEffect(() => {
    LIVE_REGION_TYPES.forEach((type) => {
      if (!document.body.querySelector(regionSelector(type))) {
        createLiveRegion(document.body, type);
      }
    });
  }, []);

  // Return function to announce a message
  return useCallback((message: string, type: AriaLiveTypes = 'polite', retain: boolean = false) => {
    if (!message) {
      return;
    }

    const { region, created } = getLiveRegion(type);

    // Each region keeps its own record, so the same sentence announced politely and then
    // assertively is two different announcements, not a repeat.
    if (isDuplicateAnnouncement(region, message)) {
      return;
    }

    recordAnnouncement(region, message);

    const node = document.createElement('div');
    node.textContent = message;

    if (created) {
      // A region that appears with its message already in it reads as new page content
      // rather than a live update, so give assistive tech a moment to start monitoring
      // the region before anything is added to it.
      setTimeout(() => region.appendChild(node), NEW_REGION_DELAY);
    } else {
      region.appendChild(node);
    }

    // Each message owns its own cleanup, so one component's announcement can never
    // clear another's. Pass retain to leave the message in the region.
    if (!retain) {
      setTimeout(() => node.remove(), NEW_REGION_DELAY + MESSAGE_LIFETIME);
    }
  }, []);
}
